import type { Funnels } from "@/@types/Analytics";
import { motion } from "framer-motion";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { PRIMARY_COLOR } from "../constants";
import { ChartCard } from "./ChartParts";

interface FunnelStep {
  name: string;
  value: number;
  percentage: number;
  amount_formatted?: string;
}

function FunnelBar({ step, index }: { step: FunnelStep; index: number }) {
  return (
    <div className="relative">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-muted-foreground">{step.name}</span>
        <div className="flex items-center gap-2">
          {step.amount_formatted && (
            <span className="text-xs text-primary">{step.amount_formatted}</span>
          )}
          <span className="text-xs font-medium text-colorText">
            {step.value} ({step.percentage?.toFixed(1)}%)
          </span>
        </div>
      </div>
      <div className="h-8 bg-zinc-800 rounded-lg overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${step.percentage}%` }}
          transition={{ duration: 0.5, delay: index * 0.1 }}
          className="h-full rounded-lg"
          style={{ backgroundColor: PRIMARY_COLOR }}
        />
      </div>
    </div>
  );
}

interface FunnelChartsProps {
  funnels?: Funnels;
  isLoading: boolean;
}

export function FunnelCharts({ funnels, isLoading }: FunnelChartsProps) {
  const { t } = useTranslation("reports");

  const checkoutSteps = useMemo((): FunnelStep[] => {
    if (!funnels?.checkout_conversion) return [];
    const { initiated_sales, approved_sales } = funnels.checkout_conversion;
    return [
      {
        name: t("reports:initiated_sales"),
        value: initiated_sales?.count ?? 0,
        percentage: initiated_sales?.percentage ?? 0,
      },
      {
        name: t("reports:approved_sales_funnel"),
        value: approved_sales?.count ?? 0,
        percentage: approved_sales?.percentage ?? 0,
      },
    ];
  }, [funnels?.checkout_conversion, t]);

  const conversion = funnels?.funnel_conversion;
  const conversionSteps = useMemo((): FunnelStep[] => {
    if (!conversion) return [];
    const { principal, orderbump, upsell_downsell } = conversion;
    return [
      { name: t("reports:front_product"), value: principal.count, ...principal },
      { name: t("reports:orderbump"), value: orderbump.count, ...orderbump },
      { name: t("reports:upsell_downsell"), value: upsell_downsell.count, ...upsell_downsell },
    ];
  }, [conversion, t]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <ChartCard
        title={t("reports:chart_checkout_funnel")}
        description={t("reports:chart_checkout_funnel_desc")}
        isLoading={isLoading}
        isEmpty={checkoutSteps.length === 0}
        heightClassName="h-[180px]"
      >
        <div className="space-y-3">
          {checkoutSteps.map((step, index) => (
            <FunnelBar key={step.name} step={step} index={index} />
          ))}
        </div>
      </ChartCard>

      <ChartCard
        title={t("reports:chart_funnel_conversion")}
        description={t("reports:chart_funnel_conversion_desc")}
        isLoading={isLoading}
        isEmpty={conversionSteps.length === 0}
        heightClassName="h-[180px]"
      >
        <div className="space-y-3">
          {conversionSteps.map((step, index) => (
            <FunnelBar key={step.name} step={step} index={index} />
          ))}
          {conversion?.total && (
            <div className="mt-4 pt-4 border-t border-colorBorder">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-colorText">Total</span>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold text-primary">
                    {conversion.total.amount_formatted}
                  </span>
                  <span className="text-sm font-semibold text-colorText">
                    {conversion.total.count} {t("reports:sales_unit")}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </ChartCard>
    </div>
  );
}
